/**
 * CABINET STYLE PICKER
 * 
 * Multi-step picker for kitchen cabinets
 * Step 1: Select door style
 * Step 2: Select finish
 * Step 3: Confirm selection
 */

import React, { useState } from 'react';
import { CABINET_STYLES, CABINET_FINISHES } from '../../hooks/useProductCatalog';

const CabinetStylePicker = ({ onSelect, currentSelection }) => {
  const [step, setStep] = useState(1);
  const [selection, setSelection] = useState({
    style: currentSelection?.style || null,
    finish: currentSelection?.finish || null,
  });

  const handleStyleSelect = (style) => {
    setSelection({ ...selection, style });
    setStep(2);
  };

  const handleFinishSelect = (finish) => {
    setSelection({ ...selection, finish });
    setStep(3);
  };

  const handleConfirm = () => {
    onSelect(selection);
  };

  return (
    <div className="cabinet-picker">
      {/* Step indicator */}
      <div className="cabinet-steps">
        <div className={`cabinet-step ${step >= 1 ? 'active' : ''}`}>
          1. Door Style
        </div>
        <div className={`cabinet-step ${step >= 2 ? 'active' : ''}`}>
          2. Finish
        </div>
        <div className={`cabinet-step ${step >= 3 ? 'active' : ''}`}>
          3. Confirm
        </div>
      </div>

      {/* Step 1: Door Style */}
      {step === 1 && (
        <div>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', color: '#1e293b' }}>
            Select Door Style
          </h3>
          <div className="style-grid">
            {CABINET_STYLES.map(style => (
              <div
                key={style.id}
                className={`style-card ${selection.style?.id === style.id ? 'selected' : ''}`}
                onClick={() => handleStyleSelect(style)}
              >
                {style.image ? (
                  <img src={style.image} alt={style.name} />
                ) : (
                  <div style={{ 
                    aspectRatio: '4/3', 
                    background: '#f1f5f9', 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    marginBottom: '0.75rem',
                    fontSize: '2rem'
                  }}>
                    🚪
                  </div>
                )}
                <p style={{ fontWeight: 500 }}>{style.name}</p>
                <p style={{ fontSize: '0.75rem', color: '#64748b' }}>
                  From ${style.base_price_lf}/LF
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2: Finish */}
      {step === 2 && (
        <div>
          <button className="back-btn" onClick={() => setStep(1)}>
            ← Change Style
          </button>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', color: '#1e293b' }}>
            Select Finish for {selection.style?.name}
          </h3>
          <div className="finish-grid">
            {CABINET_FINISHES.map(finish => (
              <div
                key={finish.id}
                className={`finish-card ${selection.finish?.id === finish.id ? 'selected' : ''}`}
                onClick={() => handleFinishSelect(finish)}
              >
                <div
                  className="finish-swatch"
                  style={{ background: finish.color }}
                />
                <p style={{ fontWeight: 500 }}>{finish.name}</p>
                <span className="finish-type">{finish.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <div className="cabinet-confirm">
          <h3 style={{ marginBottom: '1.5rem', fontSize: '1rem', color: '#1e293b' }}>
            Confirm Selection
          </h3>

          <div className="confirm-preview">
            {selection.style?.image ? (
              <img src={selection.style.image} alt="Cabinet Preview" />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f1f5f9',
                fontSize: '4rem'
              }}>
                🗄️
              </div>
            )}
          </div>

          <div className="confirm-details">
            <p><strong>Style:</strong> {selection.style?.name}</p>
            <p><strong>Finish:</strong> {selection.finish?.name} ({selection.finish?.type})</p>
          </div>

          <div className="confirm-pricing">
            <p>Base Cabinet: ${selection.style?.base_price_lf}/LF</p>
            <p>Wall Cabinet: ${selection.style?.wall_price_lf}/LF</p>
            <p>Tall Cabinet: ${selection.style?.tall_price_each} each</p>
          </div>

          <div className="confirm-actions">
            <button className="btn-secondary" onClick={() => setStep(2)}>
              ← Change Finish
            </button>
            <button className="btn-primary" onClick={handleConfirm}>
              Confirm Selection
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CabinetStylePicker;
